import React, { useState, useEffect, useRef } from 'react';
import { encode } from 'base-64';
import { useLocation, useNavigate } from 'react-router-dom';
import { RotateLoader } from 'react-spinners';
import { Table, TableBody, TableCell, Tooltip, Grid, MenuItem, Button, Divider, Dialog } from '@mui/material';
import { TableHead, TableRow, Pagination, MenuTableCell } from '../Common/Table';
import { paginationDefaults, filterSortDefaults } from './regionSchema';
import { ListParams, ListPagination, OptionsInterface } from '../Common/types';
import { isEmpty, useDebounce } from '../Common/utilities';
import { StyleSheet, commonStyles, css } from '../Common/styling';
import { useNotify } from '../Common/snackbarHooks';
import EmptyList from '../Common/Table/EmptyList';
import { SearchBar } from '../Common/SearchBar';
import { RegionInterface } from './types';
import { deleteRegion, getRegions } from './regionApi';
import { CreateFab, QuickAction } from '../Common/ButtonLinks';
import paramsFromUrl from '../Common/Hooks/paramsFromUrl';
import useGlobalPopstateListener from '../Common/Hooks/useGlobalPopstateListener';
import FilterAutocomplete from '../Common/FilterPopover/FilterAutocomplete';
import { getUsersMinimalList } from '../User/usersApi';
import InventoryIcon from '@mui/icons-material/Inventory';
import ConfirmationDialog from '../Common/Dialogs/ConfirmationDialog';
import { getResidenceClustersMinimalList } from '../ResidenceCluster/residenceClusterApi';
import { getResidenceGroupsMinimalList } from '../ResidenceGroup/residenceGroupApi';
import InfoModal from './components/InfoModal';
import ConditionalParent from '../Common/ConditionalParent';

const styles = StyleSheet.create({
  tableCell: {
    paddingTop: '2px',
    paddingBottom: '2px',
    paddingRight: '2px',
    paddingLeft: '10px',
  },
  paddingCheckbox: {
    padding: '2px',
  },
});

const Regions: React.FC = () => {
  // hooks
  const pageTopRef = useRef<null | HTMLDivElement>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { notifyInfo, notifyError } = useNotify();
  const [regions, setRegions] = useState<Array<RegionInterface>>([]);
  const [users, setUsers] = useState<Array<OptionsInterface>>([]);
  const [regionsCount, setRegionsCount] = useState<number>(0);
  const [quickActionRegion, setQuickActionRegion] = useState<RegionInterface | Record<string, never>>({});
  const [loading, setLoading] = useState(false);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [confirmationDialogVisible, setConfirmationDialogVisible] = useState(false);
  const [residenceClusters, setResidenceClusters] = useState<Array<OptionsInterface>>([]);
  const [loadingResidenceClusters, setLoadingResidenceClusters] = useState(true);
  const [residenceGroups, setResidenceGroups] = useState<Array<OptionsInterface>>([]);
  const [loadingResidenceGroups, setLoadingResidenceGroups] = useState(true);
  const [infoModalState, setInfoModalState] = useState(false);

  // get cached filters and pagination from url if any
  // otherwise load default values
  const query = paramsFromUrl('query');
  const page = paramsFromUrl('page');
  const [pagination, setPagination] = useState(
    page === '' ? (paginationDefaults as ListPagination) : (page as ListPagination)
  );
  const [listParams, setGetListParams] = useState(
    query === '' ? (filterSortDefaults as ListParams) : (query as ListParams)
  );
  const { loading: loadingRegions, debounce: fetchRegions } = useDebounce(
    (params: ListParams) => getRegions({ ...params, ...pagination }),
    ({ data }) => {
      setRegions(data.data);
      setRegionsCount(data.meta.count);
    },
    { runInitially: false }
  );

  function handleHistoryChange(event: PopStateEvent) {
    // Handle popstate event
    setPagination(paramsFromUrl('page'));
  }
  useGlobalPopstateListener(handleHistoryChange);

  const rows = [
    { id: 'name', label: 'Region' },
    { id: 'regional_manager_name', label: 'Regionchef' },
    { id: 'operational_manager_name', label: 'Verksamhetsansvarig' },
    { id: 'active_teams_count', label: 'Antal team' },
    { id: 'quick_action', label: '', notSortable: true },
  ];

  useEffect(() => {
    setPagination(pagination);
    fetchUsers();
    fetchResidenceClusters();
    fetchResidenceGroups();
    pageTopRef?.current?.scrollIntoView();
  }, []); // eslint-disable-line

  useEffect(() => {
    const query = encode(JSON.stringify(listParams));
    const page = encode(JSON.stringify(pagination));
    navigate(`/regions?query=${query}&page=${page}${location.hash}`, {
      replace: true,
    });

    fetchRegions({ ...listParams, ...pagination });
  }, [listParams, pagination]); // eslint-disable-line

  const fetchUsers = (): void => {
    setLoadingUsers(true);
    getUsersMinimalList({ hide_banned: true, filter_by_role_name: 'operational_manager' })
      .then(({ data }) => {
        setUsers(data.data);
      })
      .catch(() => notifyError('Det gick inte att hämta listan över VA användare'))
      .finally(() => setLoadingUsers(false));
  };

  const fetchResidenceClusters = (): void => {
    setLoadingResidenceClusters(true);
    getResidenceClustersMinimalList()
      .then(({ data }) => setResidenceClusters(data.data))
      .catch(() => notifyError('Det gick inte att hämta listan över grupper'))
      .finally(() => setLoadingResidenceClusters(false));
  };

  const fetchResidenceGroups = (): void => {
    setLoadingResidenceGroups(true);
    getResidenceGroupsMinimalList()
      .then(({ data }) => setResidenceGroups(data.data))
      .catch(() => notifyError('Det gick inte att hämta listan över avtalsparten'))
      .finally(() => setLoadingResidenceGroups(false));
  };

  const archiveRegion = (id: number): void => {
    setLoading(true);
    deleteRegion(id)
      .then(() => {
        notifyInfo('Nu kommer regionen att arkiveras. Du får ett mejl så snart det är klart.');
        fetchRegions({ ...listParams, ...pagination });
      })
      .catch((error: any) => notifyError(`Det gick inte att arkivera regionen: ${error}`))
      .finally(() => {
        setQuickActionRegion({});
        setLoading(false);
      });
  };

  // render
  return (
    <div ref={pageTopRef}>
      <Grid container className={css(commonStyles.headlineWrapper, commonStyles.greyRow)}>
        <Grid item xs={10} sm={10} md={10} lg={10}>
          <h1 className={css(commonStyles.headerTextStyle)}>Regioner</h1>
        </Grid>
        <Grid item xs={2} sm={2} md={2} lg={2}>
          <p style={{ textAlign: 'center', fontSize: '14px', color: 'rgba(0, 0, 0, 0.6)' }}>{regionsCount} Regioner</p>
        </Grid>
      </Grid>
      <Grid container className={css(commonStyles.searchBarWrapper)}>
        <Grid item xs={5} sm={5} md={5} lg={5}>
          <SearchBar
            setGetListParams={setGetListParams}
            listParams={listParams}
            setPagination={setPagination}
            paginationDefaults={paginationDefaults}
          />
        </Grid>
        <Grid item xs={3} sm={3} md={3} lg={3}>
          <Button
            color="primary"
            sx={{
              width: '180px',
            }}
            variant="outlined"
            onClick={(): void => {
              setInfoModalState(true);
            }}
          >
            HÄMTA INFO
          </Button>
        </Grid>
      </Grid>
      <Divider />
      <div className={css(commonStyles.filtersBarWrapper)}>
        <Grid container>
          <Grid item sx={{ display: 'flex', justifyContent: 'center' }} xs={3} sm={3} md={3} lg={3}>
            {!loadingResidenceClusters && (
              <FilterAutocomplete
                getOptionLabel={(residenceCluster) => residenceCluster.name}
                listParams={listParams}
                setGetListParams={setGetListParams}
                fieldName="filter_residence_clusters"
                values={residenceClusters.filter((r) => listParams.filter_residence_clusters?.includes(r.id))}
                label="Välj grupp"
                options={residenceClusters}
                multiple={true}
                limitTags={1}
                setPagination={setPagination}
                paginationDefaults={paginationDefaults}
                renderTags="simple"
              />
            )}
          </Grid>
          <Grid item sx={{ display: 'flex', justifyContent: 'center' }} xs={3} sm={3} md={3} lg={3}>
            {!loadingResidenceGroups && (
              <FilterAutocomplete
                getOptionLabel={(residenceGroup) => residenceGroup.name}
                listParams={listParams}
                setGetListParams={setGetListParams}
                fieldName="filter_residence_groups"
                values={residenceGroups.filter((r) => listParams.filter_residence_groups?.includes(r.id))}
                label="Välj avtalspart"
                options={
                  listParams.hide_inactive_objects
                    ? residenceGroups.filter((t) => t.is_active === true)
                    : residenceGroups
                }
                groupBy={(option) => (option.is_active ? 'Aktiva avtalspart' : 'Inaktiva avtalspart')}
                multiple={true}
                limitTags={1}
                setPagination={setPagination}
                paginationDefaults={paginationDefaults}
                renderTags="simple"
              />
            )}
          </Grid>
          <Grid item sx={{ display: 'flex', justifyContent: 'flex-end' }} xs={3} sm={3} md={3} lg={3}>
            {!loadingUsers && (
              <FilterAutocomplete
                getOptionLabel={(user) => user.name}
                listParams={listParams}
                setGetListParams={setGetListParams}
                fieldName="filter_operational_manager"
                label="Välj VA"
                values={users.filter((u: OptionsInterface) =>
                  listParams.filter_operational_manager?.includes(u.id as string)
                )}
                options={loading ? [] : users}
                multiple={true}
                limitTags={1}
                setPagination={setPagination}
                paginationDefaults={paginationDefaults}
                renderTags="simple"
              />
            )}
          </Grid>
        </Grid>
      </div>
      <Table classes={{ root: css(commonStyles.tableWrapper) }}>
        <TableHead rows={rows} listParams={listParams} setGetListParams={setGetListParams} />

        <TableBody>
          {isEmpty(regions) ? (
            <EmptyList />
          ) : (
            regions.map((row: RegionInterface, idx: number) => (
              <TableRow
                row={row}
                idx={idx}
                id={row.id?.toString()}
                key={row.id}
                onClick={(): void => {
                  if (!loading) {
                    const query = encode(JSON.stringify(listParams));
                    const page = encode(JSON.stringify(pagination));
                    navigate(`/regions?query=${query}&page=${page}#${row.id}`, { replace: true });
                    navigate(`/regions/${row.id}`);
                  }
                }}
              >
                <TableCell scope="row">
                  <b>{row.name}</b>
                </TableCell>
                <TableCell align="left" width={400}>
                  {row.regional_manager ? row.regional_manager.name : ''}
                </TableCell>
                <TableCell align="left" width={400}>
                  {row.operational_manager ? row.operational_manager.name : ''}
                </TableCell>
                <TableCell align="left" width={150}>
                  {row.active_teams_count}
                </TableCell>
                <MenuTableCell>
                  <ConditionalParent
                    on={!row.deletable}
                    parent={(children) => (
                      <Tooltip
                        title="Det går inte att arkivera regionen då den innehåller boenden. Ta bort boendena från regionen för att kunna arkivera den."
                        placement="top"
                      >
                        {children}
                      </Tooltip>
                    )}
                  >
                    <MenuItem>
                      <QuickAction
                        onClick={(): void => {
                          setQuickActionRegion(row);
                          setConfirmationDialogVisible(true);
                        }}
                        text="Arkivera"
                        icon={<InventoryIcon style={{ marginRight: '10px' }} />}
                        label="archive_region"
                        styles={commonStyles.selectionFabStyle3}
                        disabled={!row.deletable || loading}
                      />
                    </MenuItem>
                  </ConditionalParent>
                </MenuTableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>

      <ConfirmationDialog
        isVisible={confirmationDialogVisible}
        onClose={(): void => {
          setConfirmationDialogVisible(false);
          setQuickActionRegion({});
        }}
        onConfirm={(): void => {
          if (quickActionRegion.id) {
            setConfirmationDialogVisible(false);
            archiveRegion(quickActionRegion.id);
          }
        }}
        title="Arkivera region"
        message={`Vill du arkivera regionen ${quickActionRegion.name} som du valt?`}
      />

      <Pagination
        pageTopRef={pageTopRef}
        pagination={pagination}
        setPagination={setPagination}
        totalCount={regionsCount}
      />

      {loading || loadingRegions || loadingUsers ? (
        <div className={css(commonStyles.spinner)}>
          <RotateLoader loading={loading || loadingRegions || loadingUsers} />
        </div>
      ) : (
        <CreateFab link={`/regions/create`} />
      )}

      {infoModalState && (
        <Dialog
          open={infoModalState}
          onClose={(): void => {
            setInfoModalState(false);
          }}
          fullWidth={true}
          maxWidth="sm"
        >
          <InfoModal setModalState={setInfoModalState} params={listParams} />
        </Dialog>
      )}
    </div>
  );
};

export default Regions;
